"use client"

import { Dialog, DialogContent } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { useAuth } from "@clerk/nextjs"
import { useRouter } from "next/navigation"
import { Check, Minus } from "lucide-react"
import { getDatafastMetadata, dfTrackPaywallHit, dfTrackCheckoutStarted } from "@/lib/datafast"
import {
  newPaywallInstanceId,
  trackPaywallModalShown,
  trackPaywallCtaClicked,
  trackPaywallDismissed,
  trackCheckoutRedirectInitiated,
  type PaywallSurface,
  type DismissMethod,
} from "@/lib/paywall-analytics"
import { usePricingPlans } from "@/lib/hooks/use-pricing-plans"
import { countryToGeoTier, fetchCountryCode, type GeoTier } from "@/lib/geo-pricing"
import api from "@/service/api"
import { useState, useEffect, useRef } from "react"

// Map backend feature IDs to human-readable labels
const FEATURE_LABELS: Record<string, string> = {
  credits: "credit",
  clip_exports: "clip export",
}

interface PaywallModalProps {
  isOpen: boolean
  onClose: () => void
  videoTitle?: string
  creditsInfo?: {
    required: number
    available: number
    creditType?: string
  } | null
  /** Which live call site raised this wall; one funnel, sliced by origin. */
  surface: PaywallSurface
  /** The tool the user was trying to use, when the surface is a tool route. */
  toolSlug?: string
}

type Access = true | false | "limited" | string

const FEATURES: { label: string; creator: Access; business: Access }[] = [
  { label: "Credits/month",    creator: "400",      business: "800" },
  { label: "Clips/month",      creator: "150",      business: "250" },
  { label: "AI tools (51+)",   creator: "Unlimited", business: "Unlimited" },
  { label: "GIFs",             creator: "Included", business: "Included" },
  { label: "Music",            creator: "Included", business: "Included" },
  { label: "Social publishing", creator: "1 account", business: "Unlimited" },
  { label: "Brand Kit",        creator: false,      business: "Included" },
  { label: "Watermark",         creator: false,      business: false },
]

function CellValue({ value, colIndex }: { value: Access; colIndex: number }) {
  const planHighlight = colIndex === 0 ? "bg-[#CC3E21]/[0.03]" : ""

  if (value === true) {
    return (
      <div className={`px-3 py-2 flex justify-center ${planHighlight}`}>
        <span className="text-[10px] font-medium text-amber-400/80">Yes</span>
      </div>
    )
  }

  if (value === false) {
    return (
      <div className={`px-3 py-2 flex justify-center ${planHighlight}`}>
        <Minus className="h-3.5 w-3.5 text-white/10" />
      </div>
    )
  }

  const isUnlimited = value === "Unlimited"
  return (
    <div className={`px-3 py-2 flex justify-center ${planHighlight}`}>
      {isUnlimited ? (
        <Check className="h-3.5 w-3.5 text-[#CC3E21]" />
      ) : (
        <span className="text-[10px] font-medium text-muted-foreground">{value}</span>
      )}
    </div>
  )
}

export function PaywallModal({
  isOpen,
  onClose,
  videoTitle,
  creditsInfo,
  surface,
  toolSlug,
}: PaywallModalProps) {
  const { userId, getToken } = useAuth()
  const router = useRouter()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isAnnual, setIsAnnual] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const { plans, loading: plansLoading } = usePricingPlans()
  const [countryCode, setCountryCode] = useState<string | null>(null)
  const geoTier: GeoTier = countryToGeoTier(countryCode)

  useEffect(() => {
    let cancelled = false
    fetchCountryCode().then((code) => {
      if (!cancelled) setCountryCode(code)
    })
    return () => {
      cancelled = true
    }
  }, [])
  // Identity of this wall. Minted on open and carried by every event it produces, so a
  // re-render cannot look like a second wall.
  const [instanceId, setInstanceId] = useState<string>("")
  // How the current close was initiated. Dialog reports only "closed", so the surrounding
  // handlers record the method and this is read once when the close actually lands.
  const dismissMethodRef = useRef<DismissMethod>("x")
  // Whether this wall has already reported its dismissal.
  const dismissReportedRef = useRef(false)

  const trigger = creditsInfo ? "insufficient_credits" : "upgrade_prompted"
  const priceShown = !plansLoading && plans.length > 0

  const paywallContext = {
    trigger,
    tool_slug: toolSlug || "",
    surface,
    paywall_instance_id: instanceId,
  }

  useEffect(() => {
    if (!isOpen) {
      setInstanceId("")
      return
    }
    dismissReportedRef.current = false
    setInstanceId((current) => current || newPaywallInstanceId())
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      dfTrackPaywallHit({
        credit_type: creditsInfo?.creditType || "unknown",
        action_attempted: "upgrade_prompted",
      })
      // PostHog paywall_hit is tracked centrally in request.ts/streaming-request.ts
      // to avoid double-counting. Only Datafast tracking here.
    }
  }, [isOpen, creditsInfo?.creditType])

  // The offer is reported by the surface that renders it, not by the HTTP layer: only here
  // do we know whether a price actually reached the user. Waits for the plans to settle so
  // price_shown reflects the final branch rather than the loading one.
  useEffect(() => {
    if (!isOpen || !instanceId || plansLoading) return
    trackPaywallModalShown({
      trigger,
      tool_slug: toolSlug || "",
      surface,
      paywall_instance_id: instanceId,
      credit_type: creditsInfo?.creditType || "unknown",
      price_shown: plans.length > 0,
    })
  }, [isOpen, instanceId, plansLoading, plans.length, trigger, toolSlug, surface, creditsInfo?.creditType])

  // One dismissal per wall, whichever path closes it: Radix reports its own closes through
  // onOpenChange, a browser back never reaches it at all, and both must not double-count.
  const reportDismissed = (method: DismissMethod) => {
    if (!instanceId || dismissReportedRef.current) return
    dismissReportedRef.current = true
    trackPaywallDismissed({ ...paywallContext, method })
  }

  // Browser back closes the wall by leaving the route, which Radix cannot see: popstate
  // fires while the wall is still mounted, so the dismissal is reported before it goes.
  useEffect(() => {
    if (!isOpen || !instanceId) return
    const onPopState = () => reportDismissed("back")
    window.addEventListener("popstate", onPopState)
    return () => window.removeEventListener("popstate", onPopState)
  })

  // Dialog close covers the x, an outside click and Escape; report once per close.
  const handleOpenChange = (open: boolean) => {
    if (open) return
    reportDismissed(dismissMethodRef.current)
    dismissMethodRef.current = "x"
    onClose()
  }

  // An unauthenticated activation is still an activation; without this the funnel shows a
  // wall shown and no CTA, which is the blind spot this instrumentation exists to remove.
  const trackSignUpRedirect = (planId: string, ctaLabel: string) => {
    trackPaywallCtaClicked({
      ...paywallContext,
      cta_label: ctaLabel,
      destination: "sign_up",
      plan_id: planId,
      billing_period: isAnnual ? "year" : "month",
    })
  }

  const handleSubscribe = async (planId: string, ctaLabel = "Upgrade") => {
    if (!userId) {
      trackSignUpRedirect(planId, ctaLabel)
      const currentUrl = window.location.pathname + window.location.search
      router.push(`/sign-up?redirect_url=${encodeURIComponent(currentUrl)}`)
      return
    }

    setIsProcessing(true)
    setError(null)
    setStatusMessage(null)

    try {
      const token = await getToken()
      if (!token) {
        trackSignUpRedirect(planId, ctaLabel)
        const currentUrl = window.location.pathname + window.location.search
        router.push(`/sign-up?redirect_url=${encodeURIComponent(currentUrl)}`)
        return
      }

      dfTrackCheckoutStarted({ plan_id: planId, billing_period: isAnnual ? "year" : "month" })
      trackPaywallCtaClicked({
        ...paywallContext,
        cta_label: ctaLabel,
        destination: "checkout",
        plan_id: planId,
        billing_period: isAnnual ? "year" : "month",
      })

      const response = await api.billing.createCheckout(
        planId,
        `${window.location.origin}/billing?upgraded=${planId}`,
        token,
        getDatafastMetadata(),
        isAnnual ? "year" : "month",
        null,
        isAnnual ? countryCode : null
      )
      const result = response.data

      if (result.checkout_url) {
        trackCheckoutRedirectInitiated({ ...paywallContext, provider: "autumn", plan_id: planId })
        window.location.href = result.checkout_url
      } else if (result.success) {
        // Backend returned success WITHOUT a checkout URL — this happens when:
        //   - Autumn has an in-flight attach (status: "processing")
        //   - Plan change scheduled for end of period (status: "scheduled")
        //   - Plan is free/no-payment-required
        // Surface what actually happened instead of a silent redirect so the user
        // doesn't think they got scammed when they land on /billing still on Free.
        if (result.status === "processing" || result.status === "scheduled") {
          setStatusMessage(
            result.message ||
            (result.status === "scheduled"
              ? "Plan change scheduled for the end of your current billing period."
              : "Your plan change is being processed. Refresh in a few seconds.")
          )
          setIsProcessing(false)
        } else {
          onClose()
          window.location.href = `/billing?upgraded=${planId}`
        }
      } else if (result.error) {
        setError(result.error)
        setIsProcessing(false)
      }
    } catch (err: any) {
      console.error('Checkout failed:', err)
      const detail = err?.response?.data?.detail

      let message: string
      if (typeof detail === 'string') {
        message = detail.includes('Autumn') || detail.includes('not initialized')
          ? 'Billing is being set up. Please try again later.'
          : detail
      } else if (Array.isArray(detail)) {
        message = detail[0]?.msg || 'Failed to create checkout. Please try again.'
      } else {
        message = 'Failed to create checkout. Please try again.'
      }

      setError(message)
      setIsProcessing(false)
    }
  }

  // Map plan names to their IDs for the subscribe handler
  const planMap = plans.reduce((acc: Record<string, any>, plan: any) => {
    acc[plan.name] = plan
    return acc
  }, {})

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl bg-background border border-white/[0.08] shadow-2xl p-0 overflow-hidden max-h-[90vh]"
        onPointerDownOutside={() => { dismissMethodRef.current = "outside_click" }}
        onEscapeKeyDown={() => { dismissMethodRef.current = "escape" }}
      >
        <div className="overflow-y-auto max-h-[90vh] p-6">
          {/* Header */}
          <div className="text-center mb-6">
            {(() => {
              const featureLabel = creditsInfo?.creditType
                ? FEATURE_LABELS[creditsInfo.creditType] || creditsInfo.creditType.replace(/_/g, " ")
                : "video"
              const isVideo = !creditsInfo?.creditType || creditsInfo.creditType === "credits"

              return (
                <>
                  <h2 className="text-xl font-bold text-foreground">
                    {creditsInfo
                      ? isVideo ? "Need More Credits" : `${featureLabel} Limit Reached`
                      : "Upgrade to Continue"}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1.5">
                    {creditsInfo
                      ? <>Requires <span className="text-[#CC3E21] font-medium">{creditsInfo.required} {featureLabel}{creditsInfo.required !== 1 ? 's' : ''}</span> — you have <span className="font-medium">{creditsInfo.available}</span> remaining</>
                      : videoTitle ? "Unlock all features for this video" : "Choose a plan to unlock all features"
                    }
                  </p>
                </>
              )
            })()}
          </div>

          {/* Social proof */}
          <div className="flex items-center justify-center gap-1.5 mb-3 text-[11px] text-muted-foreground">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span>Trusted by 900+ creators &middot; 30-day money-back guarantee</span>
          </div>

          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                !isAnnual ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                isAnnual ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Annual
              <span className="ml-1 inline-block bg-emerald-500/20 text-emerald-400 text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                Save 40%
              </span>
            </button>
          </div>

          {/* Comparison table */}
          {plansLoading ? (
            <div className="text-center py-6 text-sm text-muted-foreground">
              <div className="inline-block w-4 h-4 border-2 border-current border-r-transparent rounded-full animate-spin mr-2" />
              Loading plans...
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-6 px-3 rounded-lg bg-destructive/5 border border-destructive/20">
              <p className="text-sm text-destructive font-medium">Pricing temporarily unavailable.</p>
              <p className="text-xs text-muted-foreground mt-1">Please refresh the page or contact support@transcriptr.ai.</p>
            </div>
          ) : (
            <>
            {/* Mobile (<sm): stacked per-plan cards. The comparison grid below needs 240px of
                fixed columns, which leaves no room for prices or tap targets at 360px. */}
            <div className="sm:hidden space-y-2">
              {(["Creator", "Business"] as const).map((name) => {
                const plan = planMap[name]
                if (!plan) return null
                const displayPrice = isAnnual && plan.annual_price ? plan.annual_price : plan.price
                return (
                  <div
                    key={name}
                    className={`rounded-lg border p-4 ${name === "Creator" ? "border-[#CC3E21]/40 bg-[#CC3E21]/5" : "border-border"}`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <div>
                        <div className="font-semibold text-sm text-foreground">
                          {name}
                          {name === "Creator" && (
                            <span className="ml-2 text-[9px] font-bold text-[#CC3E21] uppercase tracking-wider">Popular</span>
                          )}
                        </div>
                        {isAnnual && plan.annual_total && (
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            ${plan.annual_total.toLocaleString()}/yr
                          </div>
                        )}
                      </div>
                      {displayPrice != null && (
                        <div className="text-lg font-bold text-foreground whitespace-nowrap">
                          ${displayPrice}
                          <span className="text-[11px] font-normal text-muted-foreground">/mo</span>
                        </div>
                      )}
                    </div>
                    <Button
                      className={`w-full mt-3 h-11 ${name === "Creator" ? "bg-[#CC3E21] hover:bg-[#b5351b] text-white" : ""}`}
                      variant={name === "Creator" ? "default" : "outline"}
                      onClick={() => handleSubscribe(plan.id, `Get ${name}`)}
                      disabled={isProcessing}
                    >
                      {isProcessing ? "Loading..." : `Get ${name}`}
                    </Button>
                  </div>
                )
              })}
            </div>

            <div className="hidden sm:block border border-border rounded-lg overflow-hidden">
              {/* Plan headers */}
              <div className="grid grid-cols-[1fr_80px_80px] bg-card">
                <div className="p-3" />
                {(["Creator", "Business"] as const).map((name) => {
                  const plan = planMap[name]
                  const displayPrice = plan ? (isAnnual && plan.annual_price ? plan.annual_price : plan.price) : null
                  // Annual savings = (monthly price × 12) − annual total. Surface per-plan so user sees concrete $$ saved.
                  const annualSavings =
                    isAnnual && plan?.price && plan?.annual_total
                      ? Math.round(plan.price * 12 - plan.annual_total)
                      : null
                  return (
                    <div key={name} className={`p-3 text-center ${name === "Creator" ? "bg-[#CC3E21]/5" : ""}`}>
                      {name === "Creator" ? (
                        <div className="text-[9px] font-bold text-[#CC3E21] uppercase tracking-wider mb-0.5">Popular</div>
                      ) : (
                        <div className="h-[14px] mb-0.5" />
                      )}
                      <div className="font-semibold text-xs text-foreground">{name}</div>
                      {displayPrice != null && (
                        <div className="text-sm font-bold text-foreground mt-0.5">
                          ${displayPrice}<span className="text-[10px] font-normal text-muted-foreground">/mo</span>
                        </div>
                      )}
                      {isAnnual && plan?.annual_total && (
                        <div className="text-[9px] text-muted-foreground mt-0.5">
                          ${plan.annual_total.toLocaleString()}/yr
                        </div>
                      )}
                      {annualSavings && annualSavings > 0 && (
                        <div className="text-[9px] font-semibold text-emerald-400 mt-0.5">
                          Save ${annualSavings}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Feature rows */}
              {FEATURES.map((feature, i) => (
                <div
                  key={feature.label}
                  className={`grid grid-cols-[1fr_80px_80px] ${i % 2 === 0 ? "bg-transparent" : "bg-card/50"} ${i < FEATURES.length - 1 ? "border-b border-border/50" : ""}`}
                >
                  <div className="px-3 py-2 text-xs text-muted-foreground">{feature.label}</div>
                  {([feature.creator, feature.business] as const).map((val, j) => (
                    <CellValue key={j} value={val} colIndex={j} />
                  ))}
                </div>
              ))}

              {/* CTA row */}
              <div className="grid grid-cols-[1fr_80px_80px] border-t border-border bg-card">
                <div className="p-3" />
                {(["Creator", "Business"] as const).map((name) => {
                  const plan = planMap[name]
                  if (!plan) return <div key={name} className="p-3" />
                  return (
                    <div key={name} className={`p-3 flex justify-center ${name === "Creator" ? "bg-[#CC3E21]/5" : ""}`}>
                      <Button
                        size="sm"
                        className={`text-[11px] h-7 px-3 ${name === "Creator" ? "bg-[#CC3E21] hover:bg-[#b5351b] text-white" : ""}`}
                        variant={name === "Creator" ? "default" : "outline"}
                        onClick={() => handleSubscribe(plan.id, `Get ${name}`)}
                        disabled={isProcessing}
                      >
                        {isProcessing ? "Loading..." : `Get ${name}`}
                      </Button>
                    </div>
                  )
                })}
              </div>
            </div>
            </>
          )}

          {/* Inline error */}
          {error && (
            <div className="mt-3 px-3 py-2 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive text-center">
              {error}
            </div>
          )}

          {/* Status message (processing / scheduled) */}
          {statusMessage && (
            <div className="mt-3 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-sm text-emerald-400 text-center">
              {statusMessage}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground mt-4">
            <span>Cancel anytime</span>
            <span className="text-white/10">|</span>
            <span>Instant access</span>
            <span className="text-white/10">|</span>
            <span>No commitment</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
